import { caseRelRemoveApi } from 'Constants'
import React, { useState } from 'react'
import { Context } from 'Context/Context';
import { useContext } from 'react';
import { toast } from 'react-toastify';
import useIsMobile from 'Hooks/useIsMobile';
import CaseCard from './CaseCard';
import { CardStack, CardNotice, CaseBtn, EmptyState, C, Svg } from './CaseListUI';
import { StageTracker, AnswerGrid, SectionLabel, requestedAnswers, fmtDate } from './CaseStage';

// Available Cases → Requested cases (handoff §7a): the case expands inside its
// card - status tracker, waiting message, the client's answers and an inline
// "Withdraw request" confirmation. One card open at a time.
const CLOCK = ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7v5l3 2'];

function RequestedCases({attorney, cases, reqRevCountArr, setUpdateComp, emptyText}) {
    const isMobile = useIsMobile();
    const {makeApiCallFunc, userDetails} = useContext(Context);
    const userInfo = attorney ? attorney?.userId :  userDetails;
    const [openId, setOpenId] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const toggle = (id) => { setConfirmId(null); setOpenId((cur) => (cur === id ? null : id)); };

    const withdrawReq = (row) => {
        const id = row?.caseId || row?.caseDetails?._id;
        setBusyId(id);
        makeApiCallFunc(
            caseRelRemoveApi,
            { caseId: id, userId: userInfo._id },
            () => {
                setBusyId(null);
                setConfirmId(null);
                setOpenId(null);
                toast.info("You have withdrawn your request. The case is back in Available cases.");
                setUpdateComp((prev) => !prev);
            },
            (data) => { setBusyId(null); console.log(data); },
        );
    };

    const expandedFor = (row) => {
        const d = row.caseDetails || {};
        const answers = requestedAnswers(d);
        const sent = fmtDate(row.updated_at);
        const confirming = confirmId === row._id;
        const busy = busyId === (row.caseId || d._id);
        return (
            <div className={`border-t border-navy/[.1] bg-ivory flex flex-col gap-[22px] ${isMobile ? 'p-[18px]' : 'py-6 px-[30px]'}`}>
                <div className="flex flex-col gap-3.5">
                    <SectionLabel>Case status</SectionLabel>
                    <StageTracker status="requested" />
                </div>
                <div className="flex items-start gap-3 py-3.5 px-4 bg-brass/[.07] border border-brass/[.22] rounded-md">
                    <span className="flex-none mt-px flex"><Svg d={CLOCK} size={18} width={2} stroke={C.brass} /></span>
                    <div className="flex flex-col gap-1">
                        <span className="font-plexsans font-semibold text-[14px] leading-[1.3] text-navy">Waiting for client approval</span>
                        <span className="font-plexsans font-normal text-[13.5px] leading-[1.45] text-navy/[.7]">
                            Your request was sent to the client{sent ? ` on ${sent}` : ''}. We'll notify you when they approve you to see the full case.
                        </span>
                    </div>
                </div>
                {answers.length > 0 &&
                    <div className="flex flex-col gap-1.5">
                        <SectionLabel>Client's answers</SectionLabel>
                        <AnswerGrid rows={answers} cols={2} />
                    </div>}
                <div className={`flex items-center gap-3 flex-wrap ${isMobile ? 'justify-start' : 'justify-end'}`}>
                    {!confirming ?
                        <span onClick={() => setConfirmId(row._id)} className="font-plexsans font-semibold text-[13px] leading-none text-danger cursor-pointer underline">Withdraw request</span>
                        :
                        <>
                            <span className={`font-plexsans font-medium text-[13.5px] leading-[1.3] text-navy ${isMobile ? 'w-full' : 'w-auto'}`}>Withdraw your request for {d.caseNumber}?</span>
                            <button type="button" onClick={() => setConfirmId(null)} className="font-plexsans font-semibold text-[13px] leading-none text-navy bg-white border border-navy/[.25] rounded-md py-2.5 px-4 cursor-pointer">Keep request</button>
                            <button type="button" disabled={busy} onClick={() => withdrawReq(row)} className={`font-plexsans font-semibold text-[13px] leading-none text-ivory bg-danger border-0 rounded-md py-2.5 px-4 ${busy ? 'cursor-default opacity-60' : 'cursor-pointer opacity-100'}`}>
                                {busy ? 'Withdrawing…' : 'Withdraw'}
                            </button>
                        </>}
                </div>
            </div>
        );
    };

  return (
    <>
    { cases?.length ?
        <CardStack>
            {cases.map((row, key) => {
                const d = row.caseDetails;
                const deniedTimes = reqRevCountArr?.[d?._id]?.denied;
                const pendingClose = reqRevCountArr?.[d?._id]?.reqToClose;
                const team = row?.assistantId?.length ? row.assistantId[0] : null;
                const open = openId === row._id;
                return (
                    <CaseCard
                        key={row?._id || key}
                        detail={d}
                        userInfo={userInfo}
                        reqRevCountArr={reqRevCountArr}
                        onOpen={isMobile ? undefined : () => toggle(row._id)}
                        notices={(pendingClose || deniedTimes) ? <>
                            {pendingClose ? <CardNotice tone="danger">This case is pending closing by the client</CardNotice> : null}
                            {deniedTimes ? <CardNotice tone="warn">You have requested to view this case {deniedTimes} {deniedTimes === 1 ? "time" : "times"} before</CardNotice> : null}
                        </> : null}
                        footerNote={team ? <>Requested by team <b className="font-semibold text-navy">{team.firstName} {team.lastName}</b></> : null}
                        actions={<>
                            <span className={`inline-flex items-center justify-center gap-1.5 font-plexsans font-semibold text-[12px] leading-none text-brass bg-brass/[.08] border border-brass/[.3] rounded-[20px] py-[7px] px-[11px] whitespace-nowrap${isMobile ? ' self-start' : ''}`}>
                                <Svg d={CLOCK} size={12} width={2.2} />Awaiting client approval
                            </span>
                            <CaseBtn variant="secondary" onClick={() => toggle(row._id)} full={isMobile} style={{ borderColor: 'rgba(20,33,61,.3)', padding: '11px 18px', gap: '8px', ...(isMobile ? { height: '46px', fontSize: '14px' } : {}) }}>
                                {open ? 'Hide status' : 'View status'}
                                <Svg d="M6 9l6 6 6-6" size={14} width={2.2} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                            </CaseBtn>
                        </>}
                        expanded={open ? expandedFor(row) : null}
                    />
                )
            })}
        </CardStack>
        :
        <EmptyState title={emptyText?.title || 'No requested cases yet'} body={emptyText?.body || 'Cases will appear here as they move into this stage. Try changing your filters.'} />
    }
    </>
  )
}

export default RequestedCases;
